import { nextfetch } from '@/shared/api/nextfetch'

// Interfaces based on the provided API response structure
export interface TradeInFilterResponse {
	status?: string
	// Данные могут быть как во вложенной структуре data, так и в корне ответа
	data?: {
		PROPERTIES?: TradeInProperty[]
	}
	// Свойства могут быть непосредственно в корне объекта
	PROPERTIES?: TradeInProperty[]
	errors?: any[]
	code?: number
}

export interface TradeInProperty {
	ID: number
	CODE: string
	PROPERTY_TYPE: string
	DISPLAY_TYPE: string
	NAME: string
	DISPLAY_EXPANDED: boolean
	FILTER_HINT: string
	SORT: number
	VALUES: TradeInPropertyValue[]
}

export interface TradeInPropertyValue {
	ID: string
	ELEMENT_COUNT: number
	SORT: number
	CHECKED: boolean
	VALUE: string | number
	FILTERED_VALUE?: number
	NAME?: string
}

// Fetch iPhone trade-in filter options
export const fetchTradeInIphoneFilters = async (): Promise<TradeInFilterResponse> => {
	try {
		// Для отладки используем response без жесткой типизации
		const response = (await nextfetch(`index/trade-in/filter/iphone`, {
			credentials: 'include'
		})) as any

		// Обработка случая, когда API возвращает массив PROPERTIES напрямую
		if (Array.isArray(response)) {
			// Возвращаем в формате, ожидаемом в компоненте
			return { PROPERTIES: response } as TradeInFilterResponse
		}

		return response as TradeInFilterResponse
	} catch (error) {
		console.error('[tradein-api] Ошибка при получении фильтров для iPhone:', error)
		throw error
	}
}

/**
 * Получение цены trade-in по выбранным параметрам устройства
 * @param model - модель устройства
 * @param memory - объем памяти
 * @param simType - тип SIM-карты
 * @returns возвращает цену trade-in в числовом формате
 */
// Функция для создания строки фильтра в соответствии с форматом API
const createFilterString = (obj: { [key: string]: string }) => {
	const filterObj: { [key: string]: { type: string; value: string[] } } = {}
	for (const key in obj) {
		filterObj[key] = { type: '=', value: [obj[key]] }
	}
	return JSON.stringify(filterObj)
}

// Базовый URL API
const API_URL = 'https://api.iport.ru/api'

/**
 * Интерфейс для данных о трейд-ин устройстве
 */
export interface TradeInDeviceData {
	status: string
	data: {
		KOD: string
		TITLE: string
		TRADEIN_LINEYKA: string
		VSTROENNAYA_PAMYAT: string
		TRADEIN_TYPE_SIM: string
		TRADEIN_COLOR: string
		PRICE: number
	}
	errors: any[]
	code: number
}

/**
 * Получение данных о трейд-ин устройстве по ID продукта
 * @param productId ID продукта
 * @returns Данные о трейд-ин устройстве
 */
export const fetchTradeInDeviceData = async (productId: number): Promise<TradeInDeviceData | null> => {
	try {
		const response = await fetch(`${API_URL}/products/${productId}/trade-in`, {
			method: 'GET',
			credentials: 'include',
			headers: {
				Accept: 'application/json'
			}
		})

		if (!response.ok) {
			await response.body?.cancel().catch(() => {})
			console.error(`[tradein-api] Ошибка при получении данных для трейд-ин: ${response.status}`)
			return null
		}

		const data = await response.json()
		return data
	} catch (error) {
		console.error('[tradein-api] Ошибка при запросе данных трейд-ин:', error)
		return null
	}
}

export const fetchTradeInPrice = async (model: string, memory: string, simType: string): Promise<number> => {
	try {
		// Создаем параметры для фильтра
		const filterParams = {
			LIST_SECTION_CODE: 'iphone',
			TRADEIN_LINEYKA: model,
			VSTROENNAYA_PAMYAT: memory,
			TRADEIN_TYPE_SIM: simType
		}

		// Формируем строку фильтра
		const filterString = createFilterString(filterParams)

		// Формируем URL запроса с параметрами
		const requestUrl = `${API_URL}/index/trade-in/products?filter=${encodeURIComponent(filterString)}`

		// Выполняем запрос
		const response = await fetch(requestUrl, {
			method: 'GET',
			credentials: 'include',
			headers: {
				Accept: 'application/json'
			}
		})

		if (!response.ok) {
			return 0
		}

		const responseData = await response.json()

		// Проверяем формат ответа
		if (!responseData) {
			return 0
		}

		// Для типизации используем any на объектах данных
		const data = responseData as any

		// Вариант 1: если ответ - просто число
		if (typeof responseData === 'number') {
			return responseData
		}

		// Вариант 2: цена в data.data (число)
		if (data && typeof data.data === 'number') {
			return data.data
		}

		// Вариант 3: цена в data.PRICE - новый формат API
		if (data && data.data && typeof data.data.PRICE === 'number') {
			return data.data.PRICE
		}

		// Вариант 4: цена в массиве ITEMS
		if (data && data.data && Array.isArray(data.data.ITEMS) && data.data.ITEMS.length > 0) {
			const price = data.data.ITEMS[0].PRICE
			return typeof price === 'number' ? price : Number(price) || 0
		}

		// Вариант 5: напрямую в ITEMS
		if (data && Array.isArray(data.ITEMS) && data.ITEMS.length > 0) {
			const price = data.ITEMS[0].PRICE
			return typeof price === 'number' ? price : Number(price) || 0
		}

		// Если ничего не подошло, вернем 0
		return 0
	} catch (error) {
		console.error('[tradein-api] Ошибка при получении цены trade-in:', error)
		return 0
	}
}

// Submit trade-in form
export const submitTradeInForm = async (formData: Record<string, unknown>): Promise<any> => {
	return nextfetch(`b24-element-add`, {
		body: JSON.stringify(formData),
		credentials: 'include'
	})
}

export type TradeInMindboxRegisterPayload = {
	NAME: string
	PHONE: string
	EMAIL: string
	TITLE: string
	URL: string
	ADDITIONAL_INFO?: string | null
	CALLING_FORM?: string
	SEND_TEXT?: string
	SUM?: number
}

export const submitTradeInMindboxRegister = async (payload: TradeInMindboxRegisterPayload): Promise<void> => {
	await nextfetch(`trade-in/mindbox/register`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(payload),
		credentials: 'include'
	})
}
